/**
 * L'écran de l'emballage — les palettes qu'on se doit.
 *
 * Cadrage complet dans « Stafa v2 — Étape 6 : l'emballage » ; l'API est
 * `apps/api/src/emballage.ts`.
 *
 * Même découpe que partout, et c'est délibéré :
 *
 *   - `VueEmballage` ne fait que DESSINER. On lui donne un état, elle rend du
 *     HTML — ce qui permet de la regarder hors d'un navigateur, avec n'importe
 *     quel état, et c'est ce qui sert à `apercu.tsx` ;
 *   - `EcranEmballage` tient l'état, appelle l'API, et lui passe le résultat.
 *
 * Le calcul est dans `emballage.ts`, en fonctions pures et testées.
 *
 *
 * CET ÉCRAN NE SAISIT AUCUN ÉCHANGE
 * ---------------------------------------------------------------------------
 * Le cadrage §6 : « Échange choisi à la saisie de l'étape, pas sur le site. »
 * Il MONTRE les soldes et TIENT les bons. La seule écriture qu'il fait est
 * « marquer perdu » — un jugement, pas une saisie.
 *
 *
 * DEUX ÉCRANS ET NON UN CADRE COUPÉ EN DEUX
 * ---------------------------------------------------------------------------
 * La fiche d'un client prend la place entière, avec un bandeau « ← Les
 * clients ». C'est la règle posée à l'étape 3 : l'historique porte huit
 * colonnes, il ne tient pas dans une moitié d'écran.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  lireEmballage,
  lireFicheEmballage,
  marquerBonPerdu,
  type FicheEmballage,
} from './api.js';
import type { Choix } from './annuaire.js';
import {
  couverture,
  couvertureDite,
  cumuler,
  decompteEmballage,
  etatBonDit,
  filtrerClients,
  parClient,
  parPeriode,
  sensDit,
  soldeDit,
  type Bon,
  type LigneClient,
  type LigneSolde,
  type Maille,
  type Sens,
} from './emballage.js';
import { enFrancais } from './ot.js';

// ---------------------------------------------------------------------------
// Les états
// ---------------------------------------------------------------------------

type EtatListe =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly soldes: readonly LigneSolde[];
      readonly bons: readonly Bon[];
      readonly supports: readonly Choix[];
    };

type EtatFiche =
  | { readonly nom: 'aucune' }
  | { readonly nom: 'chargement'; readonly clientId: string }
  | { readonly nom: 'panne'; readonly clientId: string }
  | { readonly nom: 'prete'; readonly fiche: FicheEmballage };

type Onglet = 'mouvements' | 'bons' | 'periodes' | 'sites';

export interface ProprietesVueEmballage {
  readonly liste: EtatListe;
  readonly fiche: EtatFiche;
  readonly recherche: string;
  readonly sens: Sens;
  readonly onglet: Onglet;
  readonly maille: Maille;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandRecherche: (mot: string) => void;
  readonly quandSens: (sens: Sens) => void;
  readonly quandOnglet: (onglet: Onglet) => void;
  readonly quandMaille: (maille: Maille) => void;
  readonly quandOuvrir: (clientId: string) => void;
  readonly quandFermer: () => void;
  readonly quandPerdre: (bonId: string) => void;
}

/** Le tiret d'absence — jamais un zéro, qui se lirait « à jour ». */
function Absent() {
  return <span className="absent">—</span>;
}

const SENS: readonly { readonly cle: Sens; readonly nom: string }[] = [
  { cle: 'tout', nom: 'Tous' },
  { cle: 'nous_doit', nom: 'On nous doit' },
  { cle: 'nous_devons', nom: 'Nous devons' },
  { cle: 'solde', nom: 'Soldés' },
];

const MAILLES: readonly { readonly cle: Maille; readonly nom: string }[] = [
  { cle: 'mois', nom: 'Mois' },
  { cle: 'trimestre', nom: 'Trimestre' },
  { cle: 'annee', nom: 'Année' },
];

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export function VueEmballage(p: ProprietesVueEmballage) {
  if (p.fiche.nom !== 'aucune') return <VueFiche p={p} />;
  return <VueClients p={p} />;
}

function VueClients({ p }: { readonly p: ProprietesVueEmballage }) {
  if (p.liste.nom === 'chargement') {
    return (
      <div className="corps corps-ot">
        <p className="attente">Chargement…</p>
      </div>
    );
  }
  if (p.liste.nom === 'panne') {
    return (
      <div className="corps corps-ot">
        <p className="bandeau-refus">Les soldes d’emballage n’ont pas pu être lus.</p>
      </div>
    );
  }

  const lignes = parClient(p.liste.soldes, p.liste.bons);
  const visibles = filtrerClients(lignes, p.recherche, p.sens);

  return (
    <div className="corps corps-ot">
      <section className="liste">
        <div className="barre-outils">
          <input
            className="recherche"
            type="search"
            placeholder="Client…"
            value={p.recherche}
            onChange={(e) => {
              p.quandRecherche(e.target.value);
            }}
          />
          <label className="champ-date">
            <span className="etiquette">Sens</span>
            <select
              className="champ"
              value={p.sens}
              onChange={(e) => {
                p.quandSens(e.target.value as Sens);
              }}
            >
              {SENS.map((s) => (
                <option key={s.cle} value={s.cle}>
                  {s.nom}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* LA SECONDE MOITIÉ DU DÉCOMPTE EST LA RAISON D'ÊTRE DE L'ÉCRAN :
            les bons expirés à statuer sont du travail qui attend. Sans elle,
            la liste n'est qu'un pense-bête. */}
        <p className="decompte">{decompteEmballage(visibles)}</p>

        {visibles.length === 0 && (
          <div className="vide">
            <p>Aucun compte d’emballage à afficher.</p>
          </div>
        )}

        {visibles.length > 0 && (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="col-souple">
                    Client
                  </th>
                  <th scope="col">Sites</th>
                  <th scope="col" className="col-large">
                    Livrées
                  </th>
                  <th scope="col" className="col-large">
                    Reprises
                  </th>
                  <th scope="col">Bons</th>
                  <th scope="col" className="col-large">
                    Perdus
                  </th>
                  <th scope="col">Solde</th>
                  <th scope="col" className="col-large">
                    Dernier mouvement
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((l) => (
                  <RangeeClient key={l.client_id} l={l} p={p} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function RangeeClient({
  l,
  p,
}: {
  readonly l: LigneClient;
  readonly p: ProprietesVueEmballage;
}) {
  return (
    <tr
      className="rangee"
      tabIndex={0}
      onClick={() => {
        p.quandOuvrir(l.client_id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          p.quandOuvrir(l.client_id);
        }
      }}
    >
      <td className="col-souple">{l.client}</td>
      {/* UN CLIENT SANS ÉCHANGE AFFICHE DES TIRETS, JAMAIS DES ZÉROS. Un
          vraquier en benne n'échange pas de palette : un zéro y voudrait dire
          « à jour », ce qui est faux. */}
      <td className="code">{l.vide ? <Absent /> : l.sites}</td>
      <td className="code col-large">{l.vide ? <Absent /> : l.livrees}</td>
      <td className="code col-large">{l.vide ? <Absent /> : l.reprises}</td>
      <td className="code">
        {l.bonsEnAttente + l.bonsExpires === 0 ? (
          <Absent />
        ) : (
          <>
            {l.bonsEnAttente + l.bonsExpires}
            {l.bonsExpires > 0 && (
              <span className="secondaire">{l.bonsExpires} à statuer</span>
            )}
          </>
        )}
      </td>
      <td className="code col-large">{l.bonsPerdus === 0 ? <Absent /> : l.bonsPerdus}</td>
      {/* LE SIGNE PORTE LE SENS, la couleur ne fait que le répéter — le
          cadrage §7 interdit les aplats dans les lignes d'un tableau. */}
      <td className="code">
        {l.vide ? (
          <Absent />
        ) : (
          <>
            {soldeDit(l.solde)}
            <span className="secondaire">{sensDit(l.solde)}</span>
          </>
        )}
      </td>
      <td className="col-large">
        {l.dernierJour === null ? <Absent /> : enFrancais(l.dernierJour)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// La fiche d'un client — un écran ENTIER, quatre onglets
// ---------------------------------------------------------------------------

const ONGLETS: readonly { readonly cle: Onglet; readonly nom: string }[] = [
  { cle: 'mouvements', nom: 'Mouvements' },
  { cle: 'bons', nom: 'Bons' },
  { cle: 'periodes', nom: 'Par période' },
  { cle: 'sites', nom: 'Par site' },
];

function VueFiche({ p }: { readonly p: ProprietesVueEmballage }) {
  if (p.fiche.nom === 'chargement') {
    return (
      <div className="corps corps-ot">
        <p className="attente">Chargement…</p>
      </div>
    );
  }
  if (p.fiche.nom === 'panne') {
    return (
      <div className="corps corps-ot">
        <p className="bandeau-refus">Cette fiche n’a pas pu être lue.</p>
        <button className="bouton" type="button" onClick={p.quandFermer}>
          ← Les clients
        </button>
      </div>
    );
  }
  if (p.fiche.nom === 'aucune') return null;

  const { fiche } = p.fiche;
  const solde = fiche.soldes.reduce((n, s) => n + s.solde, 0);
  const ouverture = fiche.soldes.reduce((n, s) => n + s.ouverture, 0);
  const lignes = cumuler(fiche.mouvements, ouverture);
  const c = couverture(solde, fiche.bons);

  return (
    <div className="corps corps-ot">
      <div className="bandeau-fiche">
        <button className="retour" type="button" onClick={p.quandFermer}>
          ← Les clients
        </button>
        <h1>{fiche.client.nom}</h1>
        <p className="decompte">
          {soldeDit(solde)} — {sensDit(solde)}
          {fiche.bons.length > 0 && <> · {couvertureDite(c)}</>}
        </p>
      </div>

      <div className="sous-navigation">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            className="onglet-sous"
            type="button"
            aria-selected={p.onglet === o.cle}
            onClick={() => {
              p.quandOnglet(o.cle);
            }}
          >
            {o.nom}
          </button>
        ))}
      </div>

      {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}

      {p.onglet === 'mouvements' && (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Ordre</th>
                <th scope="col" className="col-souple">
                  Site
                </th>
                <th scope="col" className="col-large">
                  Livrées
                </th>
                <th scope="col" className="col-large">
                  Reprises
                </th>
                <th scope="col">Écart</th>
                <th scope="col" className="col-large">
                  Bon
                </th>
                <th scope="col">Solde après</th>
              </tr>
            </thead>
            <tbody>
              {/* LA LIGNE D'OUVERTURE EN TÊTE. Sans elle, l'historique
                  raconterait qu'un client ne devait rien le jour de la mise en
                  service — ce qui est faux. */}
              {ouverture !== 0 && (
                <tr>
                  <td colSpan={7}>Solde d’ouverture</td>
                  <td className="code">{soldeDit(ouverture)}</td>
                </tr>
              )}
              {lignes.map((m) => (
                <tr key={m.id}>
                  <td>{enFrancais(m.jour)}</td>
                  {/* UNE PERTE N'A PAS D'ORDRE : elle n'a eu lieu à aucun
                      arrêt. Le mot le dit, plutôt qu'une case vide. */}
                  <td className="code">
                    {m.ot_numero ?? <span className="secondaire">Perte</span>}
                  </td>
                  <td className="col-souple">{m.site}</td>
                  <td className="code col-large">{m.livrees === 0 ? <Absent /> : m.livrees}</td>
                  <td className="code col-large">
                    {m.reprises === 0 ? <Absent /> : m.reprises}
                  </td>
                  <td className="code">{soldeDit(m.ecart)}</td>
                  <td className="col-large">{m.bon_numero ?? <Absent />}</td>
                  <td className="code">{soldeDit(m.soldeApres)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {p.onglet === 'bons' && (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col">N°</th>
                <th scope="col" className="col-souple">
                  Site
                </th>
                <th scope="col">Palettes</th>
                <th scope="col" className="col-large">
                  Émis le
                </th>
                <th scope="col" className="col-large">
                  Valable jusqu’au
                </th>
                <th scope="col" className="col-large">
                  Ordre d’origine
                </th>
                <th scope="col">État</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {fiche.bons.map((b) => (
                <tr key={b.id}>
                  <td className="code">{b.numero}</td>
                  <td className="col-souple">{b.site}</td>
                  <td className="code">{b.quantite}</td>
                  <td className="col-large">{enFrancais(b.emis_le)}</td>
                  <td className="col-large">
                    {b.expire_le === null ? <Absent /> : enFrancais(b.expire_le)}
                  </td>
                  <td className="code col-large">{b.ordre_origine ?? <Absent />}</td>
                  <td>
                    {etatBonDit(b.etat)}
                    {b.perdu_par !== null && (
                      <span className="secondaire">{b.perdu_par}</span>
                    )}
                  </td>
                  <td className="actions-document">
                    {/* « PERDU » EST UNE DÉCISION HUMAINE, jamais déduite de la
                        date d'expiration : « parfois il y a un espoir ». Le
                        geste n'est donc proposé que sur un bon vivant, et il
                        écrit un mouvement de perte. */}
                    {(b.etat === 'attente' || b.etat === 'expire') && (
                      <button
                        className="bouton-discret"
                        type="button"
                        disabled={p.envoi}
                        onClick={() => {
                          p.quandPerdre(b.id);
                        }}
                      >
                        Marquer perdu
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {fiche.bons.length === 0 && (
                <tr>
                  <td colSpan={8}>Aucun bon enregistré pour ce client.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {p.onglet === 'periodes' && (
        <>
          <div className="barre-outils">
            <label className="champ-date">
              <span className="etiquette">Maille</span>
              <select
                className="champ"
                value={p.maille}
                onChange={(e) => {
                  p.quandMaille(e.target.value as Maille);
                }}
              >
                {MAILLES.map((m) => (
                  <option key={m.cle} value={m.cle}>
                    {m.nom}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="col-souple">
                    Période
                  </th>
                  <th scope="col">Mouvements</th>
                  <th scope="col" className="col-large">
                    Livrées
                  </th>
                  <th scope="col" className="col-large">
                    Reprises
                  </th>
                  <th scope="col">Écart</th>
                  <th scope="col">Solde à la fin</th>
                </tr>
              </thead>
              <tbody>
                {/* LES PÉRIODES SANS MOUVEMENT NE SONT PAS LISTÉES : écrire
                    « octobre : 0, 0, 0 » noierait les mois où il s'est passé
                    quelque chose. Le solde, lui, court par-dessus le trou. */}
                {parPeriode(fiche.mouvements, ouverture, p.maille).map((per) => (
                  <tr key={per.cle}>
                    <td className="col-souple">{per.nom}</td>
                    <td className="code">{per.mouvements}</td>
                    <td className="code col-large">{per.livrees}</td>
                    <td className="code col-large">{per.reprises}</td>
                    <td className="code">{soldeDit(per.ecart)}</td>
                    <td className="code">{soldeDit(per.soldeALaFin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {p.onglet === 'sites' && (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col" className="col-souple">
                  Site
                </th>
                <th scope="col" className="col-large">
                  Support
                </th>
                <th scope="col" className="col-large">
                  Ouverture
                </th>
                <th scope="col" className="col-large">
                  Livrées
                </th>
                <th scope="col" className="col-large">
                  Reprises
                </th>
                <th scope="col">Solde</th>
              </tr>
            </thead>
            <tbody>
              {/* C'EST ICI QUE SE VOIT CE QUE LE TOTAL CACHE : un client peut
                  devoir sur un site et être créditeur sur l'autre. */}
              {fiche.soldes.map((s) => (
                <tr key={`${s.site_id}-${s.support}`}>
                  <td className="col-souple">
                    {s.site}
                    {s.site_ville !== null && (
                      <span className="secondaire">{s.site_ville}</span>
                    )}
                  </td>
                  <td className="col-large">{s.support}</td>
                  <td className="code col-large">
                    {s.ouverture === 0 ? <Absent /> : soldeDit(s.ouverture)}
                  </td>
                  <td className="code col-large">{s.livrees}</td>
                  <td className="code col-large">{s.reprises}</td>
                  <td className="code">
                    {soldeDit(s.solde)}
                    <span className="secondaire">{sensDit(s.solde)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

export interface ProprietesEmballage {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranEmballage({ jeton, quandAccesPerdu }: ProprietesEmballage) {
  const [liste, setListe] = useState<EtatListe>({ nom: 'chargement' });
  const [fiche, setFiche] = useState<EtatFiche>({ nom: 'aucune' });
  const [recherche, setRecherche] = useState('');
  const [sens, setSens] = useState<Sens>('tout');
  const [onglet, setOnglet] = useState<Onglet>('mouvements');
  const [maille, setMaille] = useState<Maille>('mois');
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lu = await lireEmballage(j);
    if (lu.etat === 'ok') {
      setListe({ nom: 'prete', ...lu.valeur });
    } else {
      if (lu.cause === 'acces') quandAccesPerdu();
      setListe({ nom: 'panne' });
    }
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const ouvrir = useCallback(
    async (clientId: string) => {
      setFiche({ nom: 'chargement', clientId });
      setOnglet('mouvements');
      setRefus(null);
      const j = await avecJeton();
      if (j === null) return;
      const lu = await lireFicheEmballage(j, clientId);
      if (lu.etat === 'ok') {
        setFiche({ nom: 'prete', fiche: lu.valeur });
      } else {
        if (lu.cause === 'acces') quandAccesPerdu();
        setFiche({ nom: 'panne', clientId });
      }
    },
    [avecJeton, quandAccesPerdu],
  );

  /**
   * Marquer perdu recharge LA FICHE ET LA LISTE.
   *
   * L'API a écrit deux lignes — le bon et un mouvement de perte — et le solde
   * se calcule des mouvements. Ne recharger que la fiche laisserait le tableau
   * principal afficher un solde que plus rien ne justifie.
   */
  const perdre = useCallback(
    async (bonId: string) => {
      if (fiche.nom !== 'prete') return;
      setEnvoi(true);
      setRefus(null);
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const fait = await marquerBonPerdu(j, bonId);
      if (fait.etat === 'ok') {
        await ouvrir(fiche.fiche.client.id);
        await charger();
      } else {
        if (fait.cause === 'acces') quandAccesPerdu();
        setRefus(
          fait.cause === 'introuvable'
            ? 'Ce bon n’existe plus.'
            : 'Ce bon n’a pas pu être marqué perdu.',
        );
      }
      setEnvoi(false);
    },
    [fiche, avecJeton, ouvrir, charger, quandAccesPerdu],
  );

  return (
    <VueEmballage
      liste={liste}
      fiche={fiche}
      recherche={recherche}
      sens={sens}
      onglet={onglet}
      maille={maille}
      refus={refus}
      envoi={envoi}
      quandRecherche={setRecherche}
      quandSens={setSens}
      quandOnglet={setOnglet}
      quandMaille={setMaille}
      quandOuvrir={(id) => {
        void ouvrir(id);
      }}
      quandFermer={() => {
        setFiche({ nom: 'aucune' });
        setRefus(null);
      }}
      quandPerdre={(id) => {
        void perdre(id);
      }}
    />
  );
}
